import { Economy } from '@audigoal/core';
import { db } from '@/data/db';

/**
 * The award itself: what is stored when the exam is passed, and what the
 * screen reads back.
 *
 * Kept out of `certificate-card.ts` so the drawing module needs no database,
 * and out of `progress.ts` so the award is one small thing in one place.
 *
 * Stored in `kv` rather than on the profile: it is a single row that never
 * needs indexing or syncing on its own — the session that earned it is already
 * in the outbox with its accuracy, so a server can re-derive the award.
 */

const KEY = 'certificate:master';

export interface CertificateAward {
  correct: number;
  total: number;
  awardedAt: string;
  /** The name printed on it, captured at the time it was earned. */
  name: string;
}

export async function getCertificate(): Promise<CertificateAward | null> {
  const row = await db.kv.get(KEY);
  const value = row?.value as Partial<CertificateAward> | undefined;
  if (value === undefined) return null;
  if (typeof value.correct !== 'number' || typeof value.total !== 'number') return null;
  return {
    correct: value.correct,
    total: value.total,
    awardedAt: typeof value.awardedAt === 'string' ? value.awardedAt : new Date().toISOString(),
    name: typeof value.name === 'string' ? value.name : '',
  };
}

/**
 * Records a pass, once.
 *
 * A second pass with a better score replaces the first — a learner who retakes
 * it and does better should have the better certificate — but a worse retake
 * leaves the earlier one alone.
 */
export async function awardCertificate(award: CertificateAward): Promise<boolean> {
  const result = Economy.gradeCertificate(award.correct, award.total);
  if (!result.passed) return false;

  const existing = await getCertificate();
  if (existing !== null && existing.correct / existing.total >= result.score) return false;

  await db.kv.put({ key: KEY, value: award });
  // The graduation gown's unlock names this certificate.
  await db.cosmetics.put({
    cosmeticId: 'cos.graduation-gown',
    source: `certificate:${Economy.CERTIFICATE_ID}`,
  });
  return true;
}
